import { useCallback, useMemo } from 'react';
import { FormikHelpers } from 'formik';
import { useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { mixed, object as yupObject, string } from 'yup';
import { useAuth } from '@elevator/ui';
import { Submission as SubmissionType, SubmissionContent } from 'types/submissions';
import { CreditInput } from 'types/credit';
import { Service } from '@elevator/api-hooks/types/schema-types';
import { UPDATE_MY_SUBMISSION } from 'lib/graphql/mutations/submissions';
import {
  CREATE_CREDIT,
  UPDATE_CREDIT,
  DELETE_CREDIT,
} from 'lib/graphql/mutations/credits';
import { MY_SUBMISSION_BY_ORDER_NUMBER } from 'lib/graphql/queries/submissions';
import { reportError } from 'lib/errors';
import type { OrderDetailsProps } from 'molecules/OrderDetails';

const MAX_FILE_SIZE = 20971520; // ~20 MB
const SUPPORTED_IMAGE_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

export interface UseEditSubmissionParams {
  submission: SubmissionType;
  returnToSubmissions: () => void;
}

export interface UseEditSubmissionResult {
  initialValues: SubmissionContent & { credits?: CreditInput[] };
  validationSchema: ReturnType<typeof yupObject>;
  onSubmit: (
    values: SubmissionContent,
    actions: FormikHelpers<SubmissionContent>,
  ) => Promise<void>;
  removeCredit: (creditId: string | undefined, index: number) => Promise<void>;
  saveCredit: (credit: CreditInput, index: number) => Promise<void>;
  creditList: CreditInput[];
  orderDetailsProps: OrderDetailsProps;
}

/**
 * Hook that encapsulates edit-submission logic: form config, submit handler,
 * and credit create/update/delete with refetch and snackbars. Used by the
 * Submission layout to drive Formik + OrderDetails + SubmissionForm.
 */
export function useEditSubmission({
  submission,
  returnToSubmissions,
}: UseEditSubmissionParams): UseEditSubmissionResult {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const {
    submissionId,
    orderNumber,
    contentUrl,
    contentTitle,
    contentImage,
    contentDescription,
    credits,
    submissionType,
    services,
    createdAt,
    order,
  } = submission;

  const addOns: Service[] = useMemo(
    () => services?.filter((s) => s.parent) ?? [],
    [services],
  );

  const creditList: CreditInput[] = useMemo(
    () =>
      credits.map((credit) => ({
        creditId: credit.creditId,
        type: credit.creditType.typeName,
        name: credit.name,
        userId: credit.user?.userId ?? undefined,
        socialMedia: credit.socialMedia,
        avatar: credit.user?.avatar ?? undefined,
        isSubmitter: credit.user?.userId === currentUser?.userId,
      })),
    [credits, currentUser?.userId],
  );

  const initialValues = useMemo(
    () => ({
      contentUrl,
      contentTitle,
      contentImage,
      contentDescription,
      preferredWriter: '',
    }),
    [contentUrl, contentTitle, contentImage, contentDescription],
  );

  const validationSchema = useMemo(
    () =>
      yupObject({
        contentUrl: string()
          .url(`Please enter a valid url. All content links must start with 'https://'`)
          .required('A valid url is required to make a submission'),
        contentTitle: string().required('This field is required'),
        contentImage: mixed()
          .test('SupportImageTypes', 'We do not support this kind of image', (value) => {
            if (!value) return true;
            if (value && typeof value === 'object' && 'sourceUrl' in value) return true;
            if (value instanceof File)
              return SUPPORTED_IMAGE_FORMATS.includes(value.type);
            return false;
          })
          .test('MaxfileSize', 'Please provide an image with size <= 20MB', (value) => {
            if (!value) return true;
            if (value && typeof value === 'object' && 'sourceUrl' in value) return true;
            if (value instanceof File) return value.size < MAX_FILE_SIZE;
            return false;
          }),
        contentDescription: string().required('This field is required'),
      }),
    [],
  );

  const [updateMySubmission] = useMutation<any>(UPDATE_MY_SUBMISSION);
  const [createCredit] = useMutation<any>(CREATE_CREDIT);
  const [updateCredit] = useMutation<any>(UPDATE_CREDIT);
  const [deleteCredit] = useMutation<any>(DELETE_CREDIT);

  const refetchSubmission = useCallback(() => {
    return { query: MY_SUBMISSION_BY_ORDER_NUMBER, variables: { orderNumber } };
  }, [orderNumber]);

  const onSubmit = useCallback(
    async (values: SubmissionContent, actions: FormikHelpers<SubmissionContent>) => {
      const { contentTitle, contentUrl, contentDescription } = values;
      try {
        const updates: Partial<SubmissionContent> = {
          contentTitle,
          contentUrl,
          contentDescription,
        };
        if (values.contentImage instanceof File) {
          updates.contentImage = values.contentImage;
        }
        await updateMySubmission({
          variables: { id: submissionId, updates },
          refetchQueries: [refetchSubmission()],
          awaitRefetchQueries: true,
        });
        enqueueSnackbar('Submission Updated', {
          variant: 'success',
          preventDuplicate: true,
        });
      } catch (err) {
        reportError(err, {
          metaData: {
            page: 'Submission',
            operation: 'mutation updateMySubmission',
            submissionId,
            orderNumber,
          },
          user: {
            username: currentUser?.username,
            email: currentUser?.email,
          },
        });
        enqueueSnackbar('Error Saving Submission', {
          variant: 'error',
          preventDuplicate: true,
        });
      } finally {
        actions.setSubmitting(false);
      }
    },
    [
      updateMySubmission,
      submissionId,
      refetchSubmission,
      enqueueSnackbar,
      orderNumber,
      currentUser,
    ],
  );

  const removeCredit = useCallback(
    async (creditId: string | undefined, _index: number) => {
      if (!creditId) return;
      try {
        await deleteCredit({
          variables: { id: creditId },
          refetchQueries: [refetchSubmission()],
          awaitRefetchQueries: true,
        });
        enqueueSnackbar('Credit has been deleted.', {
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          variant: 'success',
        });
      } catch {
        enqueueSnackbar('Unable to delete credit.', {
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          variant: 'error',
        });
      }
    },
    [deleteCredit, refetchSubmission, enqueueSnackbar],
  );

  const saveCredit = useCallback(
    async (credit: CreditInput, creditIndex: number) => {
      const { avatar, socialMedia, ...rest } = credit;
      const creditInput = {
        ...rest,
        socialMedia: socialMedia?.map((s) => ({ type: s.type, link: s.link })) ?? [],
      };
      try {
        if (creditIndex === -1) {
          await createCredit({
            variables: { input: { ...creditInput, submissionId } },
            refetchQueries: [refetchSubmission()],
            awaitRefetchQueries: true,
          });
          enqueueSnackbar('Credit has been created.', {
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            variant: 'success',
          });
        } else {
          const creditId = credits[creditIndex].creditId;
          await updateCredit({
            variables: { id: creditId, updates: creditInput },
            refetchQueries: [refetchSubmission()],
            awaitRefetchQueries: true,
          });
          enqueueSnackbar('Credit has been updated.', {
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            variant: 'success',
          });
        }
      } catch {
        enqueueSnackbar('Unable to save credit.', {
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          variant: 'error',
        });
      }
    },
    [
      createCredit,
      updateCredit,
      submissionId,
      credits,
      refetchSubmission,
      enqueueSnackbar,
    ],
  );

  const orderDetailsProps: OrderDetailsProps = useMemo(
    () => ({
      submissionType,
      addOns,
      orderNumber,
      orderDate: createdAt,
      submissionNumber: submissionId,
      total: order?.paymentType === 'Free' ? 0 : (order?.amountPaid ?? null),
      paymentType: order?.paymentType,
    }),
    [submissionType, addOns, orderNumber, createdAt, submissionId, order],
  );

  return {
    initialValues,
    validationSchema,
    onSubmit,
    removeCredit,
    saveCredit,
    creditList,
    orderDetailsProps,
  };
}
